import { useQueries, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/vue-query';

type TansQueryOptions = {
  api: Ref<string>;
  enabled?: Ref<boolean> | ComputedRef<boolean>;
  staleTime?: number;
  gcTime?: number;
  transform?: (data: any) => any;
  headers?: Record<string, string>;
  [key: string]: any;
};

export default function useTansQuery () {
  const query = (options: TansQueryOptions) => {
    let data;
    let loading = ref(false);
    const { enabled, api, transform, headers, ...rest } = options;
    const queryKey = [rest];

    try {
      const queryOptions: UseQueryOptions = {
        queryKey,
        queryFn: async () => {
          const res = (await $fetch(api.value, headers ? { headers } : undefined)) ?? null;
          return transform && res ? transform(res) : res;
        },
        enabled
      };

      if ('staleTime' in options) {
        queryOptions.staleTime = options.staleTime;
      }
      if ('gcTime' in options) {
        queryOptions.gcTime = options.gcTime;
      }

      const queryResult = useQuery(queryOptions);

      data = queryResult.data;
      loading = queryResult.isPending;

      onServerPrefetch(async () => {
        await queryResult.suspense();
      });

      if (queryResult.error.value) {
        throw new Error(queryResult.error.value.message);
      }
    } catch (error) {
      throw createError(`Error thrown for query request: ${(error as Error).message}`);
    }

    return {
      data,
      loading
    };
  };

  const queries = (options: TansQueryOptions[] | (() => TansQueryOptions[])) => {
    const queryClient = useQueryClient();

    const mapToDefinitions = (opts: TansQueryOptions[]): UseQueryOptions[] =>
      opts.map(({ enabled, api, staleTime, gcTime, transform, headers, ...rest }) => {
        const def: UseQueryOptions = {
          queryKey: [rest],
          queryFn: async () => {
            const res = (await $fetch(api.value, headers ? { headers } : undefined)) ?? null;
            return transform && res ? transform(res) : res;
          },
          enabled
        };
        if (staleTime !== undefined) { def.staleTime = staleTime; }
        if (gcTime !== undefined) { def.gcTime = gcTime; }
        return def;
      });

    const isFactory = typeof options === 'function';
    const queryDefs = isFactory ? computed(() => mapToDefinitions(options())) : mapToDefinitions(options);

    const results = useQueries({ queries: queryDefs });

    onServerPrefetch(async () => {
      const defs = isFactory ? (queryDefs as ComputedRef<UseQueryOptions[]>).value : (queryDefs as UseQueryOptions[]);
      await Promise.all(defs.map(def => queryClient.prefetchQuery(def)));
    });

    return computed(() =>
      results.value.map(r => ({
        data: r.data,
        loading: r.isPending
      }))
    );
  };

  return { query, queries };
}
